/**
 * The Contacts tab's sections — ONE list, read by three surfaces.
 *
 * Contacts is a single page with three stacked cards rather than three routes,
 * so its "sub-views" are anchors. The primary nav's hover flyout, the tab's own
 * sub-view strip and the deep-link param all read this list, which is what keeps
 * them in step (the same arrangement `drilldownSubTabs` uses for Documents and
 * Closing).
 *
 * Deep link: `…/contacts?section=<key>`. The tab consumes the param once,
 * scrolls to the anchor and strips it, so the URL never pins a stale section.
 */
import EngineeringOutlined from "@mui/icons-material/EngineeringOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";

/** Query param the Contacts tab reads to land on one of its sections. */
export const CONTACTS_SECTION_PARAM = "section";

/**
 * Keys for the nav flyout's ACTION rows — the ones that open a sidebar instead
 * of going somewhere. Named here so the registry and the tab agree on them.
 */
export const CONTACT_ACTIONS = { ADD_CONTACT: "add-contact" };

export const CONTACT_SECTIONS = {
  KEY_ROLES: "key-roles",
  GENERAL_CONTRACTORS: "general-contractors",
  ALL_CONTACTS: "all-contacts",
};

/** The DOM id the section card carries, and what the strip scrolls to. */
export const contactsSectionAnchor = (key) => `contacts-section-${key}`;

/**
 * The strip is pinned, so a section scrolled to by `scrollIntoView` has to
 * clear it plus a little air — otherwise the card's own header lands underneath
 * the strip that sent you there. Spread onto every anchored section card.
 */
export const CONTACTS_SECTION_ANCHOR_SX = { scrollMarginTop: "76px" };

/**
 * General Contractors only applies to construction products, so the section —
 * and therefore its nav entry — is gated on the loan's product type. Same
 * hints the tab itself uses; exported so the nav flyout can't drift from the
 * page.
 */
const CONSTRUCTION_HINTS = ["fix", "flip", "ground up", "construction", "rehab"];

export const isConstructionProduct = (loanProductType) => {
  const p = String(loanProductType || "").toLowerCase();
  return CONSTRUCTION_HINTS.some((h) => p.includes(h));
};

/**
 * @param {{ isConstruction?: boolean }} ctx
 * @returns {Array<{ key: string, label: string, icon: Function }>}
 */
export const getContactsSections = (ctx = {}) =>
  [
    {
      key: CONTACT_SECTIONS.KEY_ROLES,
      label: "Key roles",
      icon: VpnKeyOutlined,
    },
    ctx.isConstruction
      ? {
          key: CONTACT_SECTIONS.GENERAL_CONTRACTORS,
          label: "General contractors",
          icon: EngineeringOutlined,
        }
      : null,
    {
      key: CONTACT_SECTIONS.ALL_CONTACTS,
      label: "All contacts",
      icon: GroupsOutlined,
    },
  ].filter(Boolean);

export default getContactsSections;
